/**
 * Submit-and-poll orchestration for a workflow run.
 *
 * The FE never rolls back partial failures — it only reads status.
 */
import type { WorkflowRunStatus } from "~/api/generated/workflow-service/model";

import { parseRunError, WorkflowRunUnverifiedError } from "../lib/errorCatalog";
import { generateIdempotencyKey } from "../lib/idempotency";
import { isTerminal, normalizeRunStatus, shouldAwaitErrorMessage } from "../lib/runStatus";
import {
  fetchRunStatusOrPending,
  isTransientStatusError,
  type RunStatusResult,
} from "./workflowRunsStatus";
import { submitWorkflowRun, type WorkflowRunContext } from "./workflowRunsSubmit";

const RUN_AWAIT_TIMEOUT_MS = 60_000;
const RUN_AWAIT_MIN_POLL_MS = 500;

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("The operation was aborted.", "AbortError"));
      return;
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("The operation was aborted.", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/** Internal sentinel — never escapes `awaitRunTermination`, which catches it and
 * falls through to the loop's own timeout error so callers see one message shape. */
class RunAwaitDeadlineExceededError extends Error {}

/**
 * Races `promise` against the remaining poll budget instead of trusting it to
 * settle on its own. `awaitRunTermination`'s `while` loop only re-checked the
 * deadline BETWEEN iterations, so one poll that never settled — observed live
 * when a token refresh mid-poll hung with no timeout of its own — froze the
 * whole await forever regardless of `timeoutMs`. The still-pending `promise`
 * is not aborted (no signal reaches that deep here); it is left to settle in
 * the background against its own `.then`, so a late resolution is handled
 * rather than an unhandled rejection.
 */
const withDeadline = <T>(promise: Promise<T>, remainingMs: number): Promise<T> => {
  if (remainingMs <= 0) {
    return Promise.reject(new RunAwaitDeadlineExceededError());
  }

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new RunAwaitDeadlineExceededError()), remainingMs);

    promise.finally(() => clearTimeout(timer)).then(resolve, reject);
  });
};

export interface SubmitAndAwaitOptions {
  definitionKey: string;
  definitionVersion?: string;
  input: Record<string, unknown>;
  /**
   * Submission envelope `context`. Defaults to `{ trigger: { type: "DIRECT" } }`
   * — the shape programmatic UI submits use. Record-page verbs can pass
   * `RECORD_ACTION` plus `subjectRef`.
   */
  context?: WorkflowRunContext;
  /** Defaults to a fresh client key per call. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Safety cap on total wait before throwing. Default 60s. */
  timeoutMs?: number;
}

export interface AwaitRunTerminationOptions {
  runId: string;
  /** Only used to name the workflow in thrown messages. */
  definitionKey: string;
  signal?: AbortSignal;
  /** Safety cap on total wait before throwing. Default 60s. */
  timeoutMs?: number;
}

/**
 * Poll an ALREADY-SUBMITTED run until it reaches a terminal status. Resolves
 * with the terminal run on success; throws on FAILED/CANCELLED (surfacing the
 * BE `errorMessage`) or when the poll deadline is exceeded.
 *
 * A caller that needs the `runId` before the run finishes — the run tracker,
 * which shows a "view run" escape hatch while it is still going — submits first
 * and watches afterwards, without duplicating the retry-after cadence or the
 * terminal-status semantics.
 */
export const awaitRunTermination = async ({
  runId,
  definitionKey,
  signal,
  timeoutMs = RUN_AWAIT_TIMEOUT_MS,
}: AwaitRunTerminationOptions): Promise<WorkflowRunStatus> => {
  const deadline = Date.now() + timeoutMs;
  let everReadable = false;
  let statusUnobservable = false;

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new DOMException("The operation was aborted.", "AbortError");
    }

    let result: RunStatusResult | null;

    try {
      result = await withDeadline(fetchRunStatusOrPending(runId, signal), deadline - Date.now());
    } catch (error) {
      if (error instanceof RunAwaitDeadlineExceededError) {
        break;
      }

      // Transient server trouble is NOT the run failing: the run keeps
      // executing on the BE while its status endpoint answers 5xx (seen live
      // during a record-service cold restart — the run went on to succeed).
      // Aborting here reported a false failure, so keep polling within the
      // deadline and only give up on the observation, never on the run.
      if (isTransientStatusError(error)) {
        statusUnobservable = true;
        await delay(RUN_AWAIT_MIN_POLL_MS, signal);

        continue;
      }

      throw error;
    }

    statusUnobservable = false;

    if (!result) {
      // Accepted but not yet queryable — keep waiting rather than reporting a
      // failure for a run that has not even started.
      await delay(RUN_AWAIT_MIN_POLL_MS, signal);

      continue;
    }

    everReadable = true;

    const { run, retryAfterMs } = result;

    if (isTerminal(run.status)) {
      if (normalizeRunStatus(run.status) !== "SUCCEEDED") {
        // The BE writes the terminal status before it persists `errorMessage`, so
        // the first FAILED poll usually has none. Throwing here reported a bare
        // `Workflow "x" FAILED.` while the real reason landed a poll later, which
        // is what the user sees in a toast — keep waiting for it.
        if (shouldAwaitErrorMessage(run)) {
          await delay(Math.max(retryAfterMs, RUN_AWAIT_MIN_POLL_MS), signal);

          continue;
        }

        throw new Error(
          run.errorMessage
            ? parseRunError(run.errorMessage).reason
            : `Workflow "${definitionKey}" ${run.status}.`,
        );
      }

      return run;
    }

    await delay(Math.max(retryAfterMs, RUN_AWAIT_MIN_POLL_MS), signal);
  }

  // Distinguish "we lost sight of the run" from "ran too long" from "never
  // showed up" — they point at different things (a struggling status service
  // vs. a slow workflow vs. a run that was accepted and then lost). The first
  // must not read as the run failing: it may well still be running.
  //
  // All three are `WorkflowRunUnverifiedError`, not a plain `Error`: the run was
  // accepted in every one of them, so callers that can show it must not bucket
  // these with a 4xx/5xx submit or a FAILED run and offer a retry that would
  // duplicate the record.
  if (statusUnobservable) {
    throw new WorkflowRunUnverifiedError(
      "statusUnobservable",
      `Workflow "${definitionKey}" status could not be confirmed — the status service kept failing. The run may still be running; check the run list before retrying.`,
    );
  }

  if (everReadable) {
    throw new WorkflowRunUnverifiedError(
      "timedOut",
      `Workflow "${definitionKey}" timed out before completing.`,
    );
  }

  throw new WorkflowRunUnverifiedError(
    "notReadable",
    `Workflow "${definitionKey}" was accepted but its run never became readable.`,
  );
};

/**
 * Submit a workflow run and poll until it reaches a terminal status. Resolves
 * with the terminal run on success; throws on FAILED/CANCELLED (surfacing the
 * BE `errorMessage`) or when the poll deadline is exceeded.
 *
 * Use for inline "fire and wait" actions that must refresh local state once the
 * run finishes (e.g. add-colour). Runs the user should be able to walk away from
 * belong in the run tracker, which submits and then watches in the background.
 */
export const submitWorkflowRunAndAwait = async ({
  definitionKey,
  definitionVersion,
  input,
  context,
  idempotencyKey,
  signal,
  timeoutMs = RUN_AWAIT_TIMEOUT_MS,
}: SubmitAndAwaitOptions): Promise<WorkflowRunStatus> => {
  const submitted = await submitWorkflowRun(
    definitionKey,
    input,
    idempotencyKey ?? generateIdempotencyKey(),
    definitionVersion,
    context,
  );

  const { runId } = submitted;

  // Accepted (2xx) with nothing to poll: the workflow may well have run, so
  // this is an unknown outcome rather than a failure — see
  // `WorkflowRunUnverifiedError`.
  if (!runId) {
    throw new WorkflowRunUnverifiedError(
      "noRunId",
      `Workflow "${definitionKey}" did not return a run id.`,
    );
  }

  return awaitRunTermination({ runId, definitionKey, signal, timeoutMs });
};
